import { OLDEST_NEWEST_COPY, SortingDirectionValues } from 'models/Sort';
import {
  SECURE_LINK_REQUEST_TYPES,
  WAITING_ROOM_REQUEST_TYPE,
  WaitingRoomRequest,
  WaitingRoomRequestType,
} from 'models/WaitingRoom';

export type WaitingRoomTableColumn =
  | 'requestor'
  | 'email'
  | 'type'
  | 'requestingAccessTo'
  | 'requested'
  | 'actions';

export type WaitingRoomRow = WaitingRoomRequest &
  Partial<Record<WaitingRoomTableColumn, unknown>>;

export const WAITING_ROOM_COLUMNS: WaitingRoomTableColumn[] = [
  'requestor',
  'email',
  'type',
  'requestingAccessTo',
  'requested',
  'actions',
];

export const HEADING_TRANSLATIONS: Record<WaitingRoomTableColumn, string> = {
  requestor: 'requestor',
  email: 'email',
  type: 'type',
  requestingAccessTo: 'requestingAccessTo',
  requested: 'requested',
  actions: '',
};

// BE field names, not column keys: this is what goes out as `$sort`. The review
// button is the only unsortable column.
export const SORTING_VALUES: Partial<Record<WaitingRoomTableColumn, string>> = {
  // `name`, not the `sortName` the other tables in the app sort by — this
  // endpoint resolves the requester's name itself.
  requestor: 'name',
  email: 'email',
  type: 'type',
  // Resolved on the BE across the two relations a request can point at: the
  // secure link's name or the buy-side group's.
  requestingAccessTo: 'requestingAccessTo',
  requested: 'requestedAt',
};

export const CUSTOM_SIZES: Partial<Record<WaitingRoomTableColumn, number>> = {
  requestor: 240,
  email: 280,
  type: 150,
  requestingAccessTo: 260,
  requested: 140,
  actions: 120,
};

// Newest first: the queue reads as an inbox, so the requests that just came in
// are the ones at the top.
export const DEFAULT_WAITING_ROOM_SORT = '-requestedAt';

// Every sortable column is offered in the sorter, in the order the table shows
// them. `useMultiSort` drops anything without a `SORTING_VALUES` entry, so the
// review button never makes it in.
export const WAITING_ROOM_SORT_COLUMNS: WaitingRoomTableColumn[] = [
  'requestor',
  'email',
  'type',
  'requestingAccessTo',
  'requested',
];

// The sorter reuses the table headers verbatim — every waiting room heading
// already reads as a full label.
export const MULTI_SORT_LABELS = HEADING_TRANSLATIONS;

// Keyed by sort field, not column key. Fields left out fall back to A-Z / Z-A
// inside useMultiSort, which is what the four text columns want.
export const MULTI_SORT_DIRECTION_COPY: Record<
  string,
  SortingDirectionValues[string]
> = {
  requestedAt: OLDEST_NEWEST_COPY,
};

export const WAITING_ROOM_LIMIT = '20';

// The waiting room is the only paginated table on its route, so the plain
// param names are free.
export const WAITING_ROOM_PAGE_PARAM = 'page';
export const WAITING_ROOM_SORT_PARAM = 'sort';

// Named after the keys the filter-options route returns, which are also the
// params the list route filters by. `requestedAt` is the exception — it has no
// options to hand out, so it is named after the field it narrows.
export const WAITING_ROOM_FILTER_PARAMS = {
  TYPES: 'types',
  REQUESTORS: 'requestors',
  EMAILS: 'emails',
  REQUESTING_ACCESS_TO: 'requestingAccessTo',
  REQUESTED: 'requestedAt',
} as const;

// The two pills the type column shows, not the four types behind them: which
// secure link request it is only matters once the seller opens it. Picking
// "Secure link" therefore filters by all three of its types at once.
export const TYPE_FILTER_VALUES: Record<string, WaitingRoomRequestType[]> = {
  buySideType: [WAITING_ROOM_REQUEST_TYPE.BUY_SIDE_INVITE],
  secureLinkType: SECURE_LINK_REQUEST_TYPES,
};

